/** Командная палитра (⌘K / Ctrl+K): переход по разделам и поиск закупок. */

import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useProcesses } from "../api/hooks";
import { useAuth, type Role } from "../auth/AuthProvider";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { useTheme } from "../theme/ThemeProvider";
import Icon, { type IconName } from "./Icon";

export interface PaletteDestination {
  to: string;
  label: string;
  icon: IconName;
  group: string;
  roles: Role[];
  keywords?: string;
  /** Раздел виден только тем, кому выдан доступ к отчётам. */
  needsReports?: boolean;
}

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: IconName;
  group: string;
  run: () => void;
}

/** Совпадение подряд идущих символов запроса в строке — как в поиске файлов. */
function fuzzyScore(haystack: string, needle: string): number {
  if (!needle) return 1;
  const text = haystack.toLowerCase();
  const query = needle.toLowerCase();
  if (text.includes(query)) return 100 - text.indexOf(query);

  let index = 0;
  let score = 0;
  for (const char of query) {
    const found = text.indexOf(char, index);
    if (found === -1) return 0;
    score += found === index ? 2 : 1;
    index = found + 1;
  }
  return score;
}

export default function CommandPalette({
  open,
  onClose,
  destinations,
}: {
  open: boolean;
  onClose: () => void;
  destinations: PaletteDestination[];
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setChoice } = useTheme();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const debounced = useDebouncedValue(query, 220);

  const role = (user?.role as Role) ?? "purchaser";

  // Поиск по закупкам подключается, только когда есть что искать.
  const searching = debounced.trim().length >= 2;
  const processes = useProcesses(
    { q: debounced.trim(), limit: 6, include_completed: true, include_requests: true },
    { enabled: open && searching },
  );

  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const items: Command[] = destinations
      .filter(
        (destination) =>
          destination.roles.includes(role) &&
          (!destination.needsReports || Boolean(user?.reports_access)),
      )
      .map((destination) => ({
        id: `nav:${destination.to}`,
        label: destination.label,
        icon: destination.icon,
        group: destination.group,
        hint: destination.keywords,
        run: () => navigate(destination.to),
      }));

    items.push(
      {
        id: "action:theme",
        label: theme === "dark" ? "Включить светлую тему" : "Включить тёмную тему",
        icon: theme === "dark" ? "sun" : "moon",
        group: "Действия",
        run: () => setChoice(theme === "dark" ? "light" : "dark"),
      },
      {
        id: "action:theme-system",
        label: "Тема как в системе",
        icon: "monitor",
        group: "Действия",
        run: () => setChoice("system"),
      },
      {
        id: "action:logout",
        label: "Выйти из системы",
        icon: "logout",
        group: "Действия",
        run: logout,
      },
    );
    return items;
  }, [destinations, role, user?.reports_access, navigate, theme, setChoice, logout]);

  const filtered = useMemo(() => {
    const trimmed = query.trim();
    const scored = commands
      .map((command) => ({
        command,
        score: Math.max(
          fuzzyScore(command.label, trimmed),
          command.hint ? fuzzyScore(command.hint, trimmed) * 0.6 : 0,
        ),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.command);
    return trimmed ? scored.slice(0, 8) : scored.slice(0, 10);
  }, [commands, query]);

  const items = processes.data?.items;

  const rows = useMemo(() => {
    const found = searching ? (items ?? []) : [];
    return [
      ...filtered.map((command) => ({ kind: "command" as const, command })),
      ...found.map((process) => ({ kind: "process" as const, process })),
    ];
  }, [filtered, items, searching]);

  useEffect(() => {
    setCursor((value) => Math.min(value, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${cursor}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (!open) return null;

  const activate = (index: number) => {
    const row = rows[index];
    if (!row) return;
    onClose();
    if (row.kind === "command") row.command.run();
    else navigate(`/processes/${row.process.process_id}`);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((value) => (value + 1) % Math.max(1, rows.length));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((value) => (value - 1 + rows.length) % Math.max(1, rows.length));
    } else if (event.key === "Enter") {
      event.preventDefault();
      activate(cursor);
    } else if (event.key === "Escape") {
      onClose();
    }
  };

  let lastGroup = "";

  return createPortal(
    <div className="fixed inset-0 z-modal flex items-start justify-center p-4 pt-[12vh]">
      <div className="fixed inset-0 bg-overlay/45 backdrop-blur-[2px]" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Быстрый переход"
        className="relative w-full max-w-xl animate-scale-in overflow-hidden rounded-xl border border-line bg-raised shadow-xl"
      >
        <div className="flex items-center gap-2.5 border-b border-line px-3.5">
          <Icon name="search" size={16} className="text-faint" />
          <input
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Перейти к разделу или найти закупку…"
            className="h-12 flex-1 bg-transparent text-md text-ink outline-none placeholder:text-faint"
          />
          <kbd className="kbd">esc</kbd>
        </div>

        <div ref={listRef} className="max-h-[22rem] overflow-y-auto scrollbar-thin p-1.5">
          {rows.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted">
              {processes.isFetching ? "Ищем…" : "Ничего не найдено"}
            </div>
          )}

          {rows.map((row, index) => {
            const group = row.kind === "command" ? row.command.group : "Закупки";
            const header = group !== lastGroup ? group : null;
            lastGroup = group;
            const active = index === cursor;

            return (
              <div key={row.kind === "command" ? row.command.id : row.process.process_id}>
                {header && (
                  <div className="px-2.5 pb-1 pt-2.5 text-2xs font-medium uppercase tracking-wider text-faint">
                    {header}
                  </div>
                )}
                <button
                  type="button"
                  data-index={index}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => activate(index)}
                  className={clsx(
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                    active ? "bg-accent-soft text-ink" : "text-ink hover:bg-sunken",
                  )}
                >
                  <Icon
                    name={row.kind === "command" ? row.command.icon : "processes"}
                    size={15}
                    className={active ? "text-accent" : "text-faint"}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {row.kind === "command" ? row.command.label : row.process.title || "Без названия"}
                  </span>
                  {row.kind === "process" && row.process.number && (
                    <span className="shrink-0 font-mono text-2xs text-faint">
                      {row.process.number}
                    </span>
                  )}
                  {active && <Icon name="arrowRight" size={13} className="shrink-0 text-accent" />}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-3 border-t border-line bg-sunken/60 px-3.5 py-2 text-2xs text-faint">
          <span className="flex items-center gap-1">
            <kbd className="kbd">↑</kbd>
            <kbd className="kbd">↓</kbd> выбор
          </span>
          <span className="flex items-center gap-1">
            <kbd className="kbd">↵</kbd> открыть
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Глобальный хук: ⌘K / Ctrl+K открывает палитру. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return { open, setOpen };
}
